import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ZpHeadLayout from './ZpHeadLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaUser,
    FaPrint,
    FaSort,
    FaEye,
    FaFileDownload
} from 'react-icons/fa';

const ZpHeadInterestedWorkDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [applicants, setApplicants] = useState([]);
    const [workDetails, setWorkDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        if (!id) return;

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const processApplicants = (work) => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key, type: 'सु.बे. अभियंता' })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key, type: 'मजूर संस्था' }))
                ];

                const interested = allRegs.filter(reg => {
                    if (reg.work_payment && reg.work_payment[work.meetingNumber]) {
                        const selected = reg.work_payment[work.meetingNumber].selectedWorks || [];
                        return selected.some(s => s.startsWith(`${work.workNumber} -`));
                    }
                    return false;
                }).map(reg => ({
                    id: reg._key,
                    name: reg.name || `${reg.firstName || ''} ${reg.middleName || ''} ${reg.surname || ''}`.trim() || reg.fullName || reg.societyName || "-",
                    phone: reg.whatsappNo || reg.mobile || reg.phoneNumber || "-",
                    type: reg.type,
                    workName: work.workName
                }));

                setApplicants(interested);
                setLoading(false);
            }
        };

        const fetchApplicants = (work) => {
            const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
            const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

            onValue(engRef, (snap) => { engData = snap.val() || {}; engDone = true; processApplicants(work); }, { onlyOnce: true });
            onValue(socRef, (snap) => { socData = snap.val() || {}; socDone = true; processApplicants(work); }, { onlyOnce: true });
        };

        // Fetch Work Details
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let foundWork = null;
                
                Object.entries(data).forEach(([key, val]) => {
                    if (foundWork) return;
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            if (key === id) foundWork = { id: key, meetingNumber: val.meetingNumber || "General", ...val };
                        } else {
                            Object.entries(val).forEach(([innerId, innerVal]) => {
                                if (innerId === id) foundWork = { id: innerId, meetingNumber: key, ...innerVal };
                            });
                        }
                    }
                });

                if (foundWork) {
                    setWorkDetails(foundWork);
                    fetchApplicants(foundWork);
                } else {
                    setLoading(false);
                }
            }
        }, { onlyOnce: true });

    }, [id]);

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4">
                <div className="animate-spin text-[#22165f] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">अर्जदार लोड होत आहेत...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-8 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#22165f] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">इंटरेस्टेड अर्जदार तपशील</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600">अर्जदार तपशील</span>
                </div>
            </div>

            {/* Work Details Card */}
            <div className="bg-[#22165f] rounded-2xl p-8 mb-8 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-10 opacity-10">
                    <FaUser size={120} />
                </div>
                <div className="relative z-10">
                    <p className="text-blue-300 text-xs font-black uppercase tracking-[0.3em] mb-3">Work Information</p>
                    <h2 className="text-2xl font-black mb-4 leading-relaxed max-w-4xl">{workDetails?.workName}</h2>
                    <div className="flex flex-wrap gap-8 text-sm">
                        <div className="flex flex-col">
                            <span className="text-blue-200 font-bold uppercase text-[10px] tracking-wider">Meeting No.</span>
                            <span className="font-extrabold">{workDetails?.meetingNumber || "-"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-blue-200 font-bold uppercase text-[10px] tracking-wider">Work ID</span>
                            <span className="font-extrabold">{workDetails?.workNumber || "-"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-blue-200 font-bold uppercase text-[10px] tracking-wider">Total Applicants</span>
                            <span className="font-extrabold text-orange-400">{applicants.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Applicants Table Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50 flex justify-between items-center">
                    <h3 className="font-black text-gray-800 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        अर्जदारांची यादी
                    </h3>
                    <button 
                        onClick={() => window.print()}
                        className="no-print bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-6 py-2 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 active:scale-95"
                    >
                        <FaPrint size={14} /> प्रिंट
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                <th className="px-8 py-5 w-20 text-center">#</th>
                                <th className="px-8 py-5 min-w-[300px]">अर्जदाराचे नाव</th>
                                <th className="px-8 py-5 min-w-[150px]">संपर्क क्रमांक</th>
                                <th className="px-8 py-5 min-w-[180px]">प्रकार</th>
                                <th className="px-8 py-5 text-center">प्रोफाइल</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {applicants.map((applicant, index) => (
                                <tr key={index} className="hover:bg-blue-50/30 transition-all group">
                                    <td className="px-8 py-5 text-center font-bold text-gray-300">{index + 1}</td>
                                    <td className="px-8 py-5">
                                        <div className="font-black text-gray-800 text-[15px] group-hover:text-blue-600 transition-colors">
                                            {applicant.name}
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 font-bold text-gray-600">{applicant.phone}</td>
                                    <td className="px-8 py-5">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${
                                            applicant.type === 'सु.बे. अभियंता' ? 'bg-indigo-50 text-indigo-600' : 'bg-orange-50 text-orange-600'
                                        }`}>
                                            {applicant.type}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <button 
                                            onClick={() => navigate(applicant.type === 'सु.बे. अभियंता' ? `/zp-head/engineers/details/${applicant.id}` : `/zp-head/societies/details/${applicant.id}`)}
                                            className="p-2 bg-gray-50 rounded-xl text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all active:scale-90"
                                        >
                                            <FaEye size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {applicants.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-8 py-20 text-center flex flex-col items-center gap-4">
                                        <div className="p-4 bg-gray-50 rounded-full text-gray-200">
                                            <FaUser size={40} />
                                        </div>
                                        <p className="text-gray-400 font-bold italic">या कामासाठी कोणतेही इंटरेस्टेड अर्ज आढळले नाहीत.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                    .bg-[#22165f] { background-color: #22165f !important; color: white !important; -webkit-print-color-adjust: exact; }
                }
            `}</style>
        </div>
    );
};

export default ZpHeadInterestedWorkDetail;
